import { InventoryHistoryResponse, InventoryItem } from "../types/inventory";

interface FetchInventoryHistoryParams {
  itemId: string;
  page?: number;
  pageSize?: number;
}

interface FetchInventoryParams {
  search?: string;
}

// Example mock inventory data for development
const mockInventory: InventoryItem[] = [
  {
    id: "1",
    name: "Paracetamol",
    category: "Pain Relief",
    quantity: 120,
    unit: "tablets",
    minThreshold: 100,
    minExpirationThreshold: 90,
    location: "Main Storage",
    expirationDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000), // 60 days from now
    supplier: "PharmaCorp",
  },
  {
    id: "2",
    name: "Ibuprofen",
    category: "Pain Relief",
    quantity: 50,
    unit: "tablets",
    minThreshold: 100,
    location: "Pharmacy",
    supplier: "MediSupply",
  },
  {
    id: "3",
    name: "Bandages",
    category: "First Aid",
    quantity: 200,
    unit: "pieces",
    minThreshold: 50,
    location: "Emergency Room",
  },
  {
    id: "4",
    name: "Amoxicillin",
    category: "Antibiotics",
    quantity: 120,
    unit: "bottles",
    minThreshold: 50,
    minExpirationThreshold: 180,
    location: "Pharmacy",
    expirationDate: new Date(Date.now() + 150 * 24 * 60 * 60 * 1000), // 150 days from now
    supplier: "MediPharm",
  },
  {
    id: "5",
    name: "Insulin",
    category: "Diabetes",
    quantity: 25,
    unit: "vials",
    minThreshold: 30,
    location: "Cold Storage",
    supplier: "DiabetesCare",
  }
];

// Example history data generator for development
const generateMockHistory = (
  itemId: string,
  page: number,
  pageSize: number
): InventoryHistoryResponse => {
  const totalEntries = 45; // Total number of history entries
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalEntries);
  
  const entries = Array.from({ length: endIndex - startIndex }, (_, index) => {
    const timestamp = new Date();
    timestamp.setHours(timestamp.getHours() - (startIndex + index));
    
    return {
      id: `hist_${itemId}_${startIndex + index}`,
      itemId,
      timestamp,
      userId: "user123",
      userName: "John Doe",
      changes: [
        {
          field: "quantity",
          oldValue: 100 - (startIndex + index),
          newValue: 100 - (startIndex + index + 1),
          displayName: "Quantity",
        },
        {
          field: "location",
          oldValue: "Warehouse A",
          newValue: "Warehouse B",
          displayName: "Location",
        },
        {
          field: "expirationDate",
          oldValue: new Date("2024-12-31"),
          newValue: new Date("2025-12-31"),
          displayName: "Expiration Date",
        },
      ],
      reason: "Stock transfer and update",
    };
  });

  return {
    entries,
    total: totalEntries,
    page,
    pageSize,
    hasMore: endIndex < totalEntries,
  };
};

export async function fetchInventory({ search }: FetchInventoryParams = {}): Promise<InventoryItem[]> {
  // TODO: Replace with actual API call
  // const response = await fetch(`/api/inventory?search=${encodeURIComponent(search || '')}`);
  // if (!response.ok) throw new Error('Failed to fetch inventory');
  // return response.json();
  
  // For development, return filtered mock data
  await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
  if (!search) return mockInventory;
  
  const searchLower = search.toLowerCase();
  return mockInventory.filter(item => 
    item.name.toLowerCase().includes(searchLower) ||
    item.category.toLowerCase().includes(searchLower) ||
    item.location?.toLowerCase().includes(searchLower)
  );
}

export async function fetchInventoryHistory({
  itemId,
  page = 1,
  pageSize = 10,
}: FetchInventoryHistoryParams): Promise<InventoryHistoryResponse> {
  // TODO: Replace with actual API call
  // const response = await fetch(`/api/inventory/${itemId}/history?page=${page}&pageSize=${pageSize}`);
  // if (!response.ok) throw new Error('Failed to fetch inventory history');
  // return response.json();
  
  // For development, return mock data
  await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
  return generateMockHistory(itemId, page, pageSize);
}
